<template>
  <div class="code-editor-container">
    <header>
    <label>{{ label }}</label>
    
    <!-- Expand Button -->
    <button class="expand-btn" @click="isExpanded = true">🔍 Expand</button>
  </header>
    
    <!-- Monaco Editor -->
    <div ref="editorContainer" class="monaco-editor" spellcheck="false" id="editor" :style="{ height: this.selectedCss === 'None' ? '500px' : '275px' }"></div>
    
    <!-- Full-Screen Pop-Out Modal -->
    <Teleport to="body">
      <div v-if="isExpanded" class="modal-overlay">
        <div class="modal-content">
          <button class="close-btn" @click="isExpanded = false">✖ Close</button>

          <div ref="popupEditorContainer" class="monaco-editor-fullscreen" spellcheck="false" id="editor"></div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, watch, nextTick } from "vue";
import * as monaco from "monaco-editor";

export default defineComponent({
  props: {
    modelValue: String, // The text (HTML or CSS) that should be shown in the editor
    label: String, // "HTML Code" or "CSS Code"
    selectedCss: String,
  },
  setup(props, { emit }) {
    const isExpanded = ref(false);
    const editorContainer = ref(null);
    const popupEditorContainer = ref(null);
    let editorInstance = null;
    let popupEditorInstance = null;

    const language = ref(props.label === "CSS Code" ? "css" : "html");

    // Function to initialize Monaco Editor
    const initializeEditor = (container, modelValue, isPopup = false) => {
      if (!container) return;

      const editor = monaco.editor.create(container, {
        value: modelValue, // ✅ Initialize with `htmlCode` or `cssCode`
        language: language.value,
        theme: "vs-dark",
        automaticLayout: true,
        wordWrap: "on",
        fontSize: 14,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
      });

      // Listen for changes inside Monaco and update Vue state
      editor.onDidChangeModelContent(() => {
        const newValue = editor.getValue();
        emit("update:modelValue", newValue); // ✅ Sync Monaco to Vue
      });

      // Store the editor instance
      if (isPopup) {
        popupEditorInstance = editor;
      } else {
        editorInstance = editor;
      }

      return editor;
    };

    // Initialize Monaco after the component is mounted
    onMounted(() => {
      editorInstance = initializeEditor(editorContainer.value, props.modelValue);
    });

    // Watch for external changes to `modelValue` and update Monaco
    watch(() => props.modelValue, (newValue) => {
  // Ensure newValue is always a string (preventing `Illegal argument` error)
  if (typeof newValue !== "string") {
    console.warn("Invalid modelValue passed to Monaco, defaulting to empty string:", newValue);
    newValue = "";
  }

  // Update main editor only if it's initialized and needs an update
  if (editorInstance && editorInstance.getValue() !== newValue) {
    editorInstance.setValue(newValue);
  }

  // Update popup editor only if it's initialized and needs an update
  if (popupEditorInstance && popupEditorInstance.getValue() !== newValue) {
    popupEditorInstance.setValue(newValue);
  }
});


    // Watch for pop-up expansion and initialize Monaco inside it
    watch(isExpanded, async (newVal) => {
      if (newVal) {
        await nextTick();
        popupEditorInstance = initializeEditor(popupEditorContainer.value, props.modelValue, true);
      }
    });

    return { isExpanded, editorContainer, popupEditorContainer };
  },
});
</script>

<style scoped>
/****** Editor Container ******/
.code-editor-container {
  background-color: #252526;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 10px;
  margin: 10px 21% 10px 10px; /* Top, Right (160px), Bottom, Left */
  position: relative;
}

/****** Monaco Editor ******/
.monaco-editor {
  width: 100%;
  height: 275px;
  border-radius: 4px;
}

.monaco-editor-fullscreen {
  width: 100%;
  height: 90vh;
}

/****** Expand Button ******/
.expand-btn {
  display: block;
  margin: 10px auto;
  background: #444;
  color: white;
  border: none;
  padding: 3px 5px;
  cursor: pointer;
  font-size: 0.9rem;
  border-radius: 4px;
  transition: background 0.2s;
}

.expand-btn:hover {
  background: #666;
}

/****** Modal Overlay ******/
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/****** Modal Content - Updated for Left Alignment ******/
.modal-content {
  width: 76%; /* Take up 80% of the screen */
  height: calc(100vh - 118px); /* Match the height of the output-area */
  background: #252526;
  padding: 20px;
  border-radius: 8px;
  position: fixed;
  top: 50px; /* Align with the top of the output area */
  left: 0; /* Left-align so it doesn't center */
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease-in-out;
  z-index: 1100;
}


/****** Close Button ******/
.close-btn {
  position: absolute;
    top: 5px;
    right: 10px;
  background: red;
  color: white;
  border: none;
  padding: 3px 5px;
  cursor: pointer;
  z-index: 10000;
}

header {
  display: flex; 
  gap: 1rem; 
  align-items: center;
  margin-top: -15px;
}

</style>
